import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { useAuth } from './AuthContext';

export type SourceKind = 'github' | 'gdrive';
export type DriveDocType = 'docs' | 'sheets' | 'slides' | 'pdf' | 'text' | 'all';

export interface RepoDriveSource {
    /** Only populated for admin callers — the server strips this from the
     *  reviewer-facing /repos payload to avoid exposing an operational
     *  Drive identifier to non-admins. */
    folderId?: string;
    folderName?: string | null;
    docTypeFilters: DriveDocType[];
}

export interface RepoSummary {
    id: string;
    owner: string;
    name: string;
    defaultBranch: string;
    lastIndexedAt: string | null;
    lastIndexedCommitSha: string | null;
    sensitiveContentLock: boolean;
    sourceKind?: SourceKind;
    driveSource?: RepoDriveSource | null;
}

export interface RepoContextValue {
    /**
     * `undefined` while loading or after account switch. Empty array when the
     * load completes and the user has no grants. Components must gate on
     * `!== undefined` before making repo-scoped API calls — see
     * `docs/development/frontend-patterns.md §Pattern 2`.
     */
    allowedRepos: RepoSummary[] | undefined;
    effectiveRepoId: string | null;
    selectRepo: (repoId: string | null) => void;
    loadError: string | null;
    refresh: () => Promise<void>;
}

const RepoContext = createContext<RepoContextValue | undefined>(undefined);

export function useRepoContext(): RepoContextValue {
    const ctx = useContext(RepoContext);
    if (!ctx) throw new Error('useRepoContext must be used within a RepoProvider');
    return ctx;
}

function lastRepoKey(accountKey: string): string {
    // Per-account so account switching doesn't leak the previous user's
    // last-selected repo into the new user's session.
    return `docreview.lastRepoId::${accountKey}`;
}

export function RepoProvider({ children }: { children: ReactNode }) {
    const { userInfo, tokenReady } = useAuth();
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const accountKey = activeAccount?.homeAccountId ?? null;

    const [allowedRepos, setAllowedRepos] = useState<RepoSummary[] | undefined>(undefined);
    const [effectiveRepoId, setEffectiveRepoId] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    const load = useCallback(
        async (signal: { cancelled: boolean }, key: string) => {
            try {
                const repos = await apiCall<RepoSummary[]>('/repos');
                if (signal.cancelled) return;
                setAllowedRepos(repos);
                setLoadError(null);
                const stored = sessionStorage.getItem(lastRepoKey(key));
                const initial =
                    (stored && repos.find((r) => r.id === stored)) ||
                    repos[0] ||
                    null;
                setEffectiveRepoId(initial ? initial.id : null);
            } catch (err) {
                if (signal.cancelled) return;
                const message = err instanceof ApiError ? err.message : (err as Error).message;
                setAllowedRepos([]);
                setLoadError(message);
            }
        },
        []
    );

    // On account change OR auth-not-ready: clear state immediately. Cancel any
    // in-flight load. Reload when ready + active account is non-null.
    useEffect(() => {
        const signal = { cancelled: false };

        // Clear immediately when the account or auth-ready state changes.
        setAllowedRepos(undefined);
        setEffectiveRepoId(null);
        setLoadError(null);

        if (!accountKey || !userInfo || !tokenReady) {
            return () => {
                signal.cancelled = true;
            };
        }
        if (userInfo.status !== 'active') {
            setAllowedRepos([]);
            return () => {
                signal.cancelled = true;
            };
        }

        void load(signal, accountKey);
        return () => {
            signal.cancelled = true;
        };
    }, [accountKey, userInfo, tokenReady, load]);

    const selectRepo = useCallback(
        (repoId: string | null) => {
            setEffectiveRepoId(repoId);
            if (!accountKey) return;
            if (repoId) sessionStorage.setItem(lastRepoKey(accountKey), repoId);
            else sessionStorage.removeItem(lastRepoKey(accountKey));
        },
        [accountKey]
    );

    const refresh = useCallback(async () => {
        if (!accountKey) return;
        await load({ cancelled: false }, accountKey);
    }, [accountKey, load]);

    const value = useMemo<RepoContextValue>(
        () => ({ allowedRepos, effectiveRepoId, selectRepo, loadError, refresh }),
        [allowedRepos, effectiveRepoId, selectRepo, loadError, refresh]
    );

    return <RepoContext.Provider value={value}>{children}</RepoContext.Provider>;
}
